/**
 * Pass 4 — Unified card renderer.
 * Renders any card by its registry ID, passing the right data and action props.
 */
import type { DashboardCardProps } from "./DashboardCard";
import type {
  DashboardSummary,
  DashboardMilestone,
  DashboardRisksResponse,
  WorkspaceSummary,
  WorkspaceHealthData,
} from "../api";

import {
  ProjectHealthCard,
  TasksInProgressCard,
  OverdueWorkCard,
  UpcomingMilestonesCard,
  OpenRisksCard,
  BudgetStatusCard,
} from "./cards";
import {
  ProjectStatusDistributionCard,
  CompletedWorkCard,
  TasksDueThisWeekCard,
  HighRisksCard,
  ProjectsOffTrackCard,
  RecentActivityCard,
} from "./addable-cards";

type Actions = DashboardCardProps["actions"];

export interface CardRendererProps {
  cardId: string;
  dashSummary: DashboardSummary | null;
  summary: WorkspaceSummary | null;
  health: WorkspaceHealthData | null;
  risks: DashboardRisksResponse | null;
  milestones: DashboardMilestone[] | null;
  loading: boolean;
  healthFailed?: boolean;
  summaryFailed?: boolean;
  dashFailed?: boolean;
  risksFailed?: boolean;
  milestonesFailed?: boolean;
  actions?: Actions;
  /** Reload the card data (the page's own loader). */
  onRetry?: () => void;
}

export function CardRenderer({
  cardId,
  dashSummary,
  summary,
  health,
  risks,
  milestones,
  loading,
  healthFailed = false,
  summaryFailed = false,
  dashFailed = false,
  risksFailed = false,
  milestonesFailed = false,
  actions,
  onRetry,
}: CardRendererProps) {
  switch (cardId) {
    case "project-health":
      return <ProjectHealthCard data={dashSummary} loading={loading} failed={dashFailed} actions={actions} onRetry={onRetry} />;
    case "tasks-in-progress":
      return (
        <TasksInProgressCard
          summary={summary}
          health={health}
          loading={loading}
          failed={healthFailed}
          actions={actions}
          onRetry={onRetry}
        />
      );
    case "overdue-work":
      return <OverdueWorkCard health={health} loading={loading} failed={healthFailed} actions={actions} onRetry={onRetry} />;
    case "upcoming-milestones":
      return (
        <UpcomingMilestonesCard
          milestones={milestones}
          loading={loading}
          failed={milestonesFailed}
          actions={actions}
          onRetry={onRetry}
        />
      );
    case "open-risks":
      return <OpenRisksCard risks={risks} loading={loading} failed={risksFailed} actions={actions} onRetry={onRetry} />;
    case "budget-status":
      return <BudgetStatusCard actions={actions} />;

    case "project-status-distribution":
      return <ProjectStatusDistributionCard data={dashSummary} loading={loading} failed={dashFailed} actions={actions} onRetry={onRetry} />;
    case "completed-work":
      return (
        <CompletedWorkCard
          summary={summary}
          health={health}
          loading={loading}
          failed={healthFailed && summaryFailed}
          actions={actions}
          onRetry={onRetry}
        />
      );
    case "tasks-due-this-week":
      return <TasksDueThisWeekCard health={health} loading={loading} failed={healthFailed} actions={actions} onRetry={onRetry} />;
    case "high-risks":
      return <HighRisksCard risks={risks} loading={loading} failed={risksFailed} actions={actions} onRetry={onRetry} />;
    case "projects-off-track":
      return <ProjectsOffTrackCard health={health} loading={loading} failed={healthFailed} actions={actions} onRetry={onRetry} />;
    case "recent-activity":
      return <RecentActivityCard health={health} loading={loading} failed={healthFailed} actions={actions} onRetry={onRetry} />;

    default:
      return null;
  }
}
